import React from 'react';
import { BattleUnitState, BattleTileState, TurnOrderEntry, HotbarSkill, AttackForecast } from '../types/battle';
import BattleGridTiles from './BattleGridTiles';
import BattleEffects, { FloatingText, PassiveAbilityNotification } from './BattleEffects';
import BattleFx, { BattleFxEntry } from './BattleFx';
import { UnitReactionState } from '../hooks/useBattleEffects';
import { getBattleTileSize } from '../utils/battleUtils';

interface BattleGridViewportProps {
  gridWidth: number;
  gridHeight: number;
  tiles: BattleTileState[];
  units: BattleUnitState[];
  selectedUnit: BattleUnitState | null;
  currentTurnUnit: TurnOrderEntry | null;
  validMoves: { x: number; y: number }[];
  validTargets: BattleUnitState[];
  skillTargets: { x: number; y: number }[];
  skillRange: { x: number; y: number }[];
  aoeArea: { x: number; y: number }[];
  weaponRange: { x: number; y: number }[];
  attackMode: boolean;
  skillMode: boolean;
  selectedSkill: HotbarSkill | null;
  itemTargets: BattleUnitState[];
  unitReactions: Record<number, UnitReactionState>;
  dyingUnits: Record<number, BattleUnitState>;
  pendingConfirmTile: { x: number; y: number } | null;
  attackForecast: AttackForecast[] | null;
  threatTiles: { x: number; y: number }[];
  onTileClick: (x: number, y: number) => void;
  onTileHover: (x: number, y: number) => void;
  onTileLeave: () => void;
  onTileInspect: (x: number, y: number) => void;

  screenShaking: boolean;
  gridContainerRef: React.RefObject<HTMLDivElement | null>;
  isDragging: boolean;
  zoomLevel: number;
  panOffset: { x: number; y: number };
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetZoom: (gridCols?: number, gridRows?: number) => void;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;

  floatingTexts: FloatingText[];
  passiveNotifications: PassiveAbilityNotification[];
  onFloatingTextComplete: (id: string) => void;
  onPassiveNotificationComplete: (id: string) => void;
  fxEntries: BattleFxEntry[];
  onFxComplete: (id: string) => void;

  showZoomControls?: boolean;
}

const BattleGridViewport: React.FC<BattleGridViewportProps> = ({
  gridWidth, gridHeight, tiles, units,
  selectedUnit, currentTurnUnit, validMoves, validTargets, skillTargets, skillRange, aoeArea,
  weaponRange, attackMode, skillMode, selectedSkill, itemTargets, unitReactions, dyingUnits,
  pendingConfirmTile,
  attackForecast,
  threatTiles,
  onTileClick, onTileHover, onTileLeave, onTileInspect,
  screenShaking, gridContainerRef, isDragging, zoomLevel, panOffset,
  handleZoomIn, handleZoomOut, handleResetZoom,
  handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave,
  onTouchStart, onTouchMove, onTouchEnd,
  floatingTexts, passiveNotifications, onFloatingTextComplete, onPassiveNotificationComplete,
  fxEntries, onFxComplete,
  showZoomControls = true,
}) => {
  return (
    <div
      className={`battle-grid-container${screenShaking ? ' battle-grid-shake' : ''}`}
      ref={gridContainerRef}
      style={{
        position: 'relative',
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {showZoomControls && (
        <div className="grid-zoom-controls">
          <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out (-)">-</button>
          <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
          <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In (+)">+</button>
          <button className="zoom-btn reset" onClick={() => handleResetZoom(gridWidth ?? 10, gridHeight ?? 10)} title="Reset View">⟲</button>
        </div>
      )}

      <div
        className="battle-grid"
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
          transformOrigin: 'center center',
          transition: isDragging ? 'none' : 'transform 0.1s ease-out'
        }}
      >
        <BattleGridTiles
          gridWidth={gridWidth}
          gridHeight={gridHeight}
          tiles={tiles}
          units={units}
          selectedUnit={selectedUnit}
          currentTurnUnit={currentTurnUnit}
          validMoves={validMoves}
          validTargets={validTargets}
          skillTargets={skillTargets}
          skillRange={skillRange}
          aoeArea={aoeArea}
          weaponRange={weaponRange}
          attackMode={attackMode}
          skillMode={skillMode}
          selectedSkill={selectedSkill}
          itemTargets={itemTargets}
          unitReactions={unitReactions}
          dyingUnits={dyingUnits}
          pendingConfirmTile={pendingConfirmTile}
          attackForecast={attackForecast}
          threatTiles={threatTiles}
          onTileClick={onTileClick}
          onTileHover={onTileHover}
          onTileLeave={onTileLeave}
          onTileInspect={onTileInspect}
        />
      </div>

      <BattleEffects
        floatingTexts={floatingTexts}
        passiveNotifications={passiveNotifications}
        onFloatingTextComplete={onFloatingTextComplete}
        onPassiveNotificationComplete={onPassiveNotificationComplete}
      />
      <BattleFx
        entries={fxEntries}
        tileSize={getBattleTileSize()}
        onComplete={onFxComplete}
      />
    </div>
  );
};

export default BattleGridViewport;
